import { useIntl } from 'react-intl';
import { Gender } from '@ianesthesie-kit/services';
import {
  AgeBucket,
  MhoSuggestion,
  MhoSuggestionRule,
  RuleGender
} from './mhoSuggestions.model';

export const GENDER_TR_KEY: Record<RuleGender, string> = {
  ANY: 'app.all',
  MALE: 'app.user.gender.male',
  FEMALE: 'app.user.gender.female'
};

export function ageBucketLabel(b: AgeBucket): string | null {
  switch (b) {
    case 'ANY':
      return null;
    case 'A_0_7':
      return '0-7';
    case 'A_7_14':
      return '7-14';
    case 'A_14_18':
      return '14-18';
    case 'A_18_40':
      return '18-40';
    case 'A_40_65':
      return '40-65';
    case 'A_65_PLUS':
      return '65+';
  }
}

export function ageBucketForAge(age: number): Exclude<AgeBucket, 'ANY'> {
  if (age < 7) {
    return 'A_0_7';
  }
  if (age < 14) {
    return 'A_7_14';
  }
  if (age < 18) {
    return 'A_14_18';
  }
  if (age < 40) {
    return 'A_18_40';
  }
  if (age < 65) {
    return 'A_40_65';
  }
  return 'A_65_PLUS';
}

export function ruleGenderFromPatientGender(
  g?: Gender
): Exclude<RuleGender, 'ANY'> | null {
  if (g === Gender.MALE) {
    return 'MALE';
  }
  if (g === Gender.FEMALE) {
    return 'FEMALE';
  }
  return null;
}

export function rulesMatchPatient(
  rules: MhoSuggestionRule[],
  patient: { gender?: Gender; age?: number }
): boolean {
  const patientBucket =
    patient.age != null ? ageBucketForAge(patient.age) : null;
  const patientGender = ruleGenderFromPatientGender(patient.gender);
  return rules.some((rule) => {
    const genderOk = rule.gender === 'ANY' || rule.gender === patientGender;
    const ageOk = rule.ageBucket === 'ANY' || rule.ageBucket === patientBucket;
    return genderOk && ageOk;
  });
}

export function useFilterSummary(
  gender: RuleGender,
  ageBucket: AgeBucket
): string {
  const intl = useIntl();
  const genderLabel =
    gender === 'ANY'
      ? intl.formatMessage({
          id: 'doctor-dashboard.hospitalParams.mhoSuggestions.filter.anySex'
        })
      : intl.formatMessage({ id: GENDER_TR_KEY[gender] });
  const ageLabel =
    ageBucketLabel(ageBucket) ??
    intl.formatMessage({
      id: 'doctor-dashboard.hospitalParams.mhoSuggestions.filter.anyAge'
    });
  return `${genderLabel}, ${ageLabel}`;
}

export function displayLabel(
  s: Pick<MhoSuggestion, 'code' | 'snomedCode' | 'freeTextLabel'>
): string {
  return s.freeTextLabel ?? s.snomedCode ?? s.code ?? '';
}

export function buildGlobalOrderAfterDrag(
  globalList: MhoSuggestion[],
  visibleNewOrder: MhoSuggestion[],
  isVisible: (s: MhoSuggestion) => boolean
): number[] {
  const visiblePositions = globalList
    .map((s, i) => (isVisible(s) ? i : null))
    .filter((i): i is number => i !== null);
  const result = [...globalList];
  visiblePositions.forEach((pos, idx) => {
    result[pos] = visibleNewOrder[idx];
  });
  return result.map((s) => s.id);
}
